import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, SearchX, RefreshCw } from "lucide-react";
import { Header } from "@/components/Header";
import { HeroShowcase } from "@/components/HeroShowcase";
import { ModCard } from "@/components/ModCard";
import { FilterSidebar } from "@/components/FilterSidebar";
import { ModDetailModal } from "@/components/ModDetailModal";
import { GetGzdoom } from "@/components/GetGzdoom";
import { fetchMods, fetchStats, fetchFilters, registerDownload } from "@/lib/api";

const DEFAULTS = { category: "All", sourcePort: "All", multiplayer: null, sort: "downloads" };

export default function Store() {
  const [view, setView] = useState("vault");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [state, setState] = useState(DEFAULTS);
  const [mods, setMods] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [newReleases, setNewReleases] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    Promise.all([fetchStats(), fetchFilters(), fetchMods({ sort: "rating" })]).then(
      ([s, f, top]) => {
        setStats(s);
        setFilters(f);
        setFeatured(top.find((m) => m.featured) || top[0]);
      }
    );
    fetchMods({ source: "auto" })
      .then(setNewReleases)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort: state.sort };
    if (debounced) params.search = debounced;
    if (state.category !== "All") params.category = state.category;
    if (state.sourcePort !== "All") params.source_port = state.sourcePort;
    if (state.multiplayer) params.multiplayer = true;
    fetchMods(params)
      .then(setMods)
      .finally(() => setLoading(false));
  }, [debounced, state]);

  const onChange = (patch) => setState((p) => ({ ...p, ...patch }));
  const onReset = () => {
    setState(DEFAULTS);
    setSearch("");
  };

  const handleDownload = useCallback(async (mod) => {
    toast.success(`Downloading ${mod.title}`, {
      description: "Redirecting to the original host in a new tab...",
    });
    try {
      const res = await registerDownload(mod.id);
      setStats((s) => (s ? { ...s, total_downloads: s.total_downloads + 1 } : s));
      window.open(res.download_url || mod.download_url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(mod.download_url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const noFilters = !debounced && state.category === "All" && state.sourcePort === "All" && !state.multiplayer;

  const closeModal = useCallback(() => setSelected(null), []);

  const navigate = (v) => {
    setView(v);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header
        search={search}
        onSearch={setSearch}
        stats={stats}
        view={view}
        onNavigate={navigate}
      />

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {view === "gzdoom" ? (
          <GetGzdoom />
        ) : (
          <>
            {noFilters && featured && (
              <div className="mb-8">
                <HeroShowcase mod={featured} onDownload={handleDownload} onView={setSelected} />
              </div>
            )}

            {noFilters && newReleases.length > 0 && (
              <section data-testid="new-releases-section" className="mb-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <h2 className="font-pixel text-lg sm:text-xl text-white uppercase tracking-wide">
                      Fresh from /idgames
                    </h2>
                    <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#f59e0b]/50 bg-[#f59e0b]/10 font-mono text-[9px] tracking-widest uppercase text-[#f59e0b]">
                      <RefreshCw className="w-3 h-3" />
                      Auto · Weekly
                    </span>
                  </div>
                  <span className="font-mono text-xs text-gray-500">
                    {newReleases.length} new
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
                  {newReleases.map((mod, i) => (
                    <ModCard
                      key={mod.id}
                      mod={mod}
                      index={i}
                      onDownload={handleDownload}
                      onView={setSelected}
                    />
                  ))}
                </div>
              </section>
            )}

            <div className="flex flex-col lg:flex-row gap-6">
          <FilterSidebar
            filters={filters}
            category={state.category}
            sourcePort={state.sourcePort}
            multiplayer={state.multiplayer}
            sort={state.sort}
            onChange={onChange}
            onReset={onReset}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-pixel text-lg sm:text-xl text-white uppercase tracking-wide">
                {debounced ? "Search Results" : "Curated Mods"}
              </h2>
              <span data-testid="results-count" className="font-mono text-xs text-gray-500">
                {mods.length} WAD{mods.length !== 1 ? "s" : ""}
              </span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin text-[#ef4444]" />
              </div>
            ) : mods.length === 0 ? (
              <div
                data-testid="empty-state"
                className="flex flex-col items-center justify-center py-24 text-center"
              >
                <SearchX className="w-12 h-12 text-gray-700 mb-3" />
                <p className="font-pixel text-sm text-gray-400 mb-1">No WADs found</p>
                <p className="font-mono text-xs text-gray-600">
                  Try adjusting your filters or search terms.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
                {mods.map((mod, i) => (
                  <ModCard
                    key={mod.id}
                    mod={mod}
                    index={i}
                    onDownload={handleDownload}
                    onView={setSelected}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
          </>
        )}
      </main>

      <footer className="border-t border-[#2e2a36] mt-12">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-pixel text-xs text-gray-500">
            WAD<span className="text-[#84cc16]">DROP</span>
          </span>
          <p className="font-mono text-[11px] text-gray-600 text-center">
            A community catalog of Doom mods. All downloads link to their original hosts.
          </p>
        </div>
      </footer>

      <ModDetailModal
        mod={selected}
        onClose={closeModal}
        onDownload={handleDownload}
      />
    </div>
  );
}
